import React, { useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

import { logoutUser } from '../../api/api';
import { deactivateUserAccount, deleteUserAccount } from '../Preview/api';

type DeleteAccountModalProps = {
  show: boolean;
  onHide: () => void;
  initialMode?: 'delete' | 'deactivate';
};

const DeleteAccountModal: React.FC<DeleteAccountModalProps> = ({
  show,
  onHide,
  initialMode = 'deactivate',
}) => {
  const navigate = useNavigate();
  const [deleteMode, setDeleteMode] = useState<'delete' | 'deactivate'>(initialMode);
  const [confirmText, setConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const handleClose = () => {
    if (isDeleting) return;
    onHide();
  };

  const handleConfirm = async () => {
    try {
      setIsDeleting(true);
      if (deleteMode === 'delete') {
        if (confirmText !== 'DELETE') {
          toast.warn('Type DELETE to confirm.');
          return;
        }
        await deleteUserAccount();
        toast.success('Account deleted.');
      } else {
        await deactivateUserAccount();
        toast.success('Account deactivated.');
      }
      logoutUser();
      navigate('/', { replace: true });
    } catch (err) {
      console.error(err);
      toast.error('Action failed. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton={!isDeleting}>
        <Modal.Title>
          {deleteMode === 'delete' ? 'Delete account' : 'Deactivate account'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3">
          <Form.Check
            type="radio"
            id="delete-mode-deactivate"
            name="delete-mode"
            label="Deactivate (can be reactivated later)"
            checked={deleteMode === 'deactivate'}
            onChange={() => setDeleteMode('deactivate')}
          />
          <div className="text-muted small ms-4 mb-2">
            Your profile becomes invisible and logins are blocked. Data is retained.
          </div>
          <Form.Check
            type="radio"
            id="delete-mode-delete"
            name="delete-mode"
            label="Delete permanently"
            checked={deleteMode === 'delete'}
            onChange={() => setDeleteMode('delete')}
          />
          <div className="text-muted small ms-4">
            Irreversible. Your profile, uploads, and personal data will be removed.
          </div>
        </div>
        {deleteMode === 'delete' && (
          <>
            <div className="border rounded p-2 bg-light mb-2 small">
              To confirm, type <strong>DELETE</strong> below.
            </div>
            <Form.Control
              type="text"
              placeholder="Type DELETE to confirm"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
            />
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={isDeleting}>
          Cancel
        </Button>
        <Button
          variant={deleteMode === 'delete' ? 'danger' : 'warning'}
          onClick={() => void handleConfirm()}
          disabled={isDeleting || (deleteMode === 'delete' && confirmText !== 'DELETE')}
        >
          {isDeleting ? (
            <>
              <Loader2 className="us-spin me-1" size={14} />
              Working…
            </>
          ) : deleteMode === 'delete' ? (
            'Delete account'
          ) : (
            'Deactivate'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DeleteAccountModal;
